'use client';

import { fetchMarketingCoupons, receiveMarketingCoupons } from '@/api/marketingPages';
import { couponDateTransform } from '@/components/Coupon/handler';
import Image from '@/components/Image';
import { toast } from '@/components/Toast';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import appCommunicationManager, {
  EAppCommunicationFuncName,
} from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { useLocale } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type {
  MarketingCouponBlockConfig,
  MarketingCouponConfig,
  MarketingRuntimeCoupon,
} from '../../../types';
import {
  getMarketingCouponDateRangeText,
  getMarketingCouponDiscountLines,
  isUsefulMarketingCouponText,
  normalizeMarketingCouponText,
} from '../couponText';
import { MARKETING_BRAND_PRODUCTS_SECTION_ID } from './marketingAnniversaryUtils';
import { MarketingSwiperArrowButton } from './MarketingSwiperArrowButton';

const MARKETING_COUPON_MODULE_NAME = '优惠券模块';
const MARKETING_COUPON_LOGIN_PENDING_KEY = 'marketing-coupon-login-pending:v2';
const MARKETING_COUPON_ENGLISH_LOCALES = new Set(['en', 'uk', 'ca']);
const MARKETING_COUPON_WIDE_WAP_LOCALES = new Set(['de', 'fr']);
const MARKETING_COUPON_SAVED_ICON = '/coupon/marketing-coupon-saved.png';
const MARKETING_COUPON_SAVED_ICON_BY_LOCALE: Record<string, string> = {
  de: '/coupon/marketing-coupon-saved.de.svg',
  fr: '/coupon/marketing-coupon-saved.fr.svg',
};
const MARKETING_SHIPPING_COUPON_MODEL_TYPE = 4;

export function hasMarketingCouponList(couponConfig?: MarketingCouponConfig) {
  return Boolean(
    couponConfig?.visible && Array.isArray(couponConfig.coupons) && couponConfig.coupons.length > 0,
  );
}

function isMarketingShippingCoupon(coupon: MarketingRuntimeCoupon) {
  return Number(coupon.model_type) === MARKETING_SHIPPING_COUPON_MODEL_TYPE;
}

function getMarketingCouponSavedIcon(locale: string) {
  return MARKETING_COUPON_SAVED_ICON_BY_LOCALE[locale] || MARKETING_COUPON_SAVED_ICON;
}

interface MarketingCouponStripProps {
  couponConfig?: MarketingCouponConfig;
  couponBlock?: MarketingCouponBlockConfig;
  marketingPageId: number;
  isPreview: boolean;
  isWap: boolean;
  isWebPage?: boolean;
  inAppH5?: boolean;
  placement?: 'banner' | 'flow' | 'block';
  couponExposureModuleName?: string;
  onCouponExposure?: (
    couponInfo: { id?: number | string | null; name?: string | null },
    index: number,
    moduleName: string,
  ) => void;
  onCouponClick?: (
    couponInfo: { id?: number | string | null; name?: string | null },
    index: number,
    moduleName: string,
    buttonName: string,
  ) => void;
  onButtonClick?: (moduleName: string, buttonName: string) => void;
}

interface MarketingCouponItem {
  id: number | string;
  name: string;
  sourceCoupon: MarketingRuntimeCoupon;
  discountText: string;
  discountLines: string[];
  title: string;
  shippingDescription: string;
  description: string;
  label: string;
  ctaText: string;
  status: 'available' | 'saved' | 'disabled';
  isCollectible: boolean;
  isCollecting: boolean;
}

interface MarketingCouponCardProps {
  coupon: MarketingCouponItem;
  index: number;
  isWap: boolean;
  isSingleFlowCoupon: boolean;
  isDoubleFlowCoupon: boolean;
  isBannerPlacement: boolean;
  isBlockPlacement: boolean;
  shouldWrapWapDiscountText: boolean;
  shouldUseWideWapCoupon: boolean;
  cardClassName: string;
  discountClassName: string;
  titleClassName: string;
  descriptionClassName: string;
  labelClassName: string;
  ctaClassName: string;
  savedIconSrc: string;
  notchBackgroundColor?: string;
  couponExposureModuleName: string;
  onCouponCollect?: (coupon: MarketingCouponItem) => void;
  onCouponExposure?: MarketingCouponStripProps['onCouponExposure'];
}

function MarketingCouponCard({
  coupon,
  index,
  isWap,
  isSingleFlowCoupon,
  isDoubleFlowCoupon,
  isBannerPlacement,
  isBlockPlacement,
  shouldWrapWapDiscountText,
  shouldUseWideWapCoupon,
  cardClassName,
  discountClassName,
  titleClassName,
  descriptionClassName,
  labelClassName,
  ctaClassName,
  savedIconSrc,
  notchBackgroundColor,
  couponExposureModuleName,
  onCouponCollect,
  onCouponExposure,
}: MarketingCouponCardProps) {
  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });
  const hasTrackedRef = useRef(false);
  const isWapBlockPlacement = isBlockPlacement && isWap;
  const shouldStackDiscountText =
    ((isBlockPlacement && !isWapBlockPlacement) || (isWap && isSingleFlowCoupon)) &&
    coupon.discountLines.length > 1;
  const isSavedCoupon = isBlockPlacement && coupon.status === 'saved';
  const isDisabledCoupon = isBlockPlacement && coupon.status === 'disabled';
  const isActionableCoupon = coupon.isCollectible || isSavedCoupon;
  const blockCtaStateClassName = isSavedCoupon
    ? 'border border-[#808080] bg-white text-primary-black'
    : isDisabledCoupon
      ? 'bg-primary-black text-white opacity-60'
      : 'bg-primary-black text-white';

  useEffect(() => {
    if (!inView || !onCouponExposure || hasTrackedRef.current) return;

    hasTrackedRef.current = true;
    onCouponExposure(
      {
        id: coupon.id,
        name: coupon.name,
      },
      index,
      couponExposureModuleName,
    );
  }, [coupon.id, coupon.name, couponExposureModuleName, inView, index, onCouponExposure]);

  if (isWapBlockPlacement) {
    const wapDiscountText =
      coupon.discountLines.length > 1 ? coupon.discountLines.join(' ') : coupon.discountText;

    return (
      <div ref={ref} className={cardClassName}>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-[28px] left-[-6px] size-[12px] rounded-full"
          style={{ backgroundColor: notchBackgroundColor }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-[28px] right-[-6px] size-[12px] rounded-full"
          style={{ backgroundColor: notchBackgroundColor }}
        />
        {coupon.label && <div className={`${labelClassName} z-20`}>{coupon.label}</div>}
        {isSavedCoupon && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute right-0 top-0 z-0 h-[36px] w-[46px]"
          >
            <Image
              src={savedIconSrc}
              alt=""
              width={46}
              height={36}
              className="h-full w-full object-contain"
            />
          </div>
        )}
        <div className="relative z-10 flex min-h-0 flex-1 flex-col items-center justify-center gap-[4px] pt-[8px] text-center">
          <div
            className={`${discountClassName} text-center ${
              shouldWrapWapDiscountText
                ? `${
                    shouldUseWideWapCoupon ? 'max-w-[120px]' : 'max-w-[96px]'
                  } whitespace-normal [overflow-wrap:anywhere]`
                : 'whitespace-nowrap'
            }`}
          >
            {wapDiscountText}
          </div>
          {coupon.shippingDescription && (
            <div className={descriptionClassName}>{coupon.shippingDescription}</div>
          )}
          {coupon.title && <div className={titleClassName}>{coupon.title}</div>}
        </div>
        {coupon.ctaText && (
          <button
            type="button"
            className={`relative z-20 ${ctaClassName} ${blockCtaStateClassName} ${
              isActionableCoupon ? 'cursor-pointer' : 'cursor-default'
            }`}
            disabled={isDisabledCoupon || coupon.isCollecting}
            onClick={() => onCouponCollect?.(coupon)}
          >
            {coupon.isCollecting ? '...' : coupon.ctaText}
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} className={cardClassName}>
      {isSavedCoupon && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-0 top-0 z-0 h-[36px] w-[46px]"
        >
          <Image
            src={savedIconSrc}
            alt=""
            width={46}
            height={36}
            className="h-full w-full object-contain"
          />
        </div>
      )}
      {coupon.label && <div className={`${labelClassName} z-20`}>{coupon.label}</div>}
      <div
        className={`relative z-10 ${discountClassName} shrink-0 ${
          shouldStackDiscountText ? 'whitespace-normal' : 'whitespace-nowrap'
        } text-center`}
      >
        {shouldStackDiscountText ? (
          <span className="flex flex-col items-center justify-center">
            {coupon.discountLines.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </span>
        ) : (
          coupon.discountText
        )}
      </div>
      <div
        className={`relative z-10 ${
          isBannerPlacement
            ? 'ml-[24px] flex min-w-0 flex-col items-start justify-center'
            : isBlockPlacement
              ? 'flex min-w-0 flex-1 flex-col items-start justify-center gap-[8px]'
              : isSingleFlowCoupon
                ? 'ml-[26px] flex min-w-0 flex-1 flex-col items-start justify-center'
                : ''
        }`}
      >
        {coupon.title && <div className={titleClassName}>{coupon.title}</div>}
        {coupon.shippingDescription && (
          <div
            className={`${descriptionClassName} ${
              isBannerPlacement || isBlockPlacement ? '' : 'min-[1025px]:hidden'
            }`}
          >
            {coupon.shippingDescription}
          </div>
        )}
        {coupon.description && (
          <div
            className={`${descriptionClassName} ${
              isBannerPlacement || isBlockPlacement ? '' : 'min-[1025px]:hidden'
            }`}
          >
            {coupon.description}
          </div>
        )}
      </div>
      {isBlockPlacement && coupon.ctaText && (
        <button
          type="button"
          className={`relative z-20 ${ctaClassName} ${blockCtaStateClassName} ${
            isActionableCoupon ? 'cursor-pointer' : 'cursor-default'
          }`}
          disabled={isDisabledCoupon || coupon.isCollecting}
          onClick={() => onCouponCollect?.(coupon)}
        >
          {coupon.isCollecting ? '...' : coupon.ctaText}
        </button>
      )}
    </div>
  );
}

export function MarketingCouponStrip({
  couponConfig,
  couponBlock,
  marketingPageId,
  isPreview,
  isWap,
  isWebPage = !isWap,
  inAppH5 = false,
  placement = 'flow',
  couponExposureModuleName = MARKETING_COUPON_MODULE_NAME,
  onCouponExposure,
  onCouponClick,
  onButtonClick,
}: MarketingCouponStripProps) {
  const locale = useLocale();
  const tCoupons = useTranslations('coupons');
  const tCouponComponent = useTranslations('components.coupon');
  const tMarketing = useTranslations('activity.campaigns.marketing');
  const currency = useStore((state) => state.currency);
  const token = useStore((state) => state.token);
  const clientTokenReady = useStore((state) => state.clientTokenReady);
  const setClientTokenReady = useStore((state) => state.setClientTokenReady);
  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);
  const setQuickCouponDialog = useStore((state) => state.setQuickCouponDialog);
  const isBannerPlacement = placement === 'banner';
  const isBlockPlacement = placement === 'block';
  const [clientCouponConfig, setClientCouponConfig] = useState<MarketingCouponConfig>();
  const [claimedCouponIds, setClaimedCouponIds] = useState<string[]>([]);
  const [collectingCouponIds, setCollectingCouponIds] = useState<string[]>([]);
  const couponSwiperRef = useRef<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const isLoggedIn = clientTokenReady && Boolean(token);

  useEffect(() => {
    if (!isBlockPlacement || !clientTokenReady || !token || isPreview || !marketingPageId) {
      return;
    }

    const pendingValue = sessionStorage.getItem(MARKETING_COUPON_LOGIN_PENDING_KEY);
    if (!pendingValue) {
      return;
    }

    const receivePendingCoupons = async () => {
      let pending: { marketingPageId?: number; couponIds?: Array<number | string> } = {};
      try {
        pending = JSON.parse(pendingValue);
      } catch (error) {
        logger.error('Marketing coupon pending payload parse failed:', error);
      }

      const couponIds = Array.isArray(pending.couponIds) ? pending.couponIds : [];
      sessionStorage.removeItem(MARKETING_COUPON_LOGIN_PENDING_KEY);

      if (pending.marketingPageId !== marketingPageId || couponIds.length === 0) {
        return;
      }

      try {
        const response = await receiveMarketingCoupons({ marketingPageId, couponIds });
        const successIds = (response.success_coupon_ids || []).map(String);

        if (successIds.length > 0) {
          setClaimedCouponIds((prev) => Array.from(new Set([...prev, ...successIds])));
          toast.success(tMarketing('coupon.claimAllSuccess', { count: successIds.length }));
        } else {
          toast.error(response.failed_reasons?.[0]?.message || tMarketing('coupon.unavailable'));
        }
      } catch (error) {
        logger.error('Marketing coupon login receive failed:', error);
        toast.error(tMarketing('coupon.getFailed'));
      }
    };

    receivePendingCoupons();
  }, [clientTokenReady, isBlockPlacement, isPreview, marketingPageId, tMarketing, token]);

  useEffect(() => {
    if (!isBlockPlacement || !clientTokenReady || !token || !marketingPageId) {
      return;
    }

    let cancelled = false;
    fetchMarketingCoupons({ marketingPageId, isPreview })
      .then((response) => {
        if (cancelled) {
          return;
        }

        const coupons = Array.isArray(response.coupon_list) ? response.coupon_list : [];
        setClientCouponConfig({
          ...couponConfig,
          coupon_ids: Array.isArray(response.coupon_ids) ? response.coupon_ids : [],
          coupons,
          visible: response.enabled === true && coupons.length > 0,
        });
      })
      .catch((error) => {
        logger.error('Marketing coupon status refresh failed:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [clientTokenReady, couponConfig, isBlockPlacement, isPreview, marketingPageId, token]);

  const scrollToBrandProductsSection = useCallback(() => {
    const target = document.getElementById(MARKETING_BRAND_PRODUCTS_SECTION_ID);
    if (!target) {
      return;
    }

    target.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  const trackCouponButtonClick = useCallback(
    (buttonName: string) => {
      if (!isBlockPlacement) {
        return;
      }

      onButtonClick?.(couponExposureModuleName, buttonName);
    },
    [couponExposureModuleName, isBlockPlacement, onButtonClick],
  );

  const openCouponLoginDialog = useCallback(
    (coupons: MarketingCouponItem[]) => {
      const couponIds = coupons.map((coupon) => coupon.id);
      const couponIdList = couponIds.map(String);

      sessionStorage.setItem(
        MARKETING_COUPON_LOGIN_PENDING_KEY,
        JSON.stringify({
          marketingPageId,
          couponIds,
        }),
      );

      const canOpenAppQuickRegistrationDialog =
        inAppH5 &&
        typeof window !== 'undefined' &&
        Boolean((window as any)[EAppCommunicationFuncName.SHOW_DIALOG]);

      if (canOpenAppQuickRegistrationDialog) {
        let callbackName = '';
        callbackName = appCommunicationManager.registerCallback(
          EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
          async (appToken) => {
            try {
              logger.log('Marketing coupon App login token:', appToken);
              if (appToken !== 'ClearToken') {
                setToken(appToken);
                setClientTokenReady(true);
                await refreshAccountInfo();
              } else {
                setToken('');
                setUserInfo({});
                setClientTokenReady(true);
              }
            } finally {
              if (callbackName) {
                appCommunicationManager.removeCallback(callbackName);
              }
            }
          },
          {
            dialogName: 'QuickRegistrationDialog',
            backToken: true,
            arguments: { eventType: 'campaign_coupon', couponIdList },
          },
          EAppCommunicationFuncName.SHOW_DIALOG,
        );
        return;
      }

      setQuickCouponDialog({
        show: true,
        couponList: [],
        eventType: 'campaign_coupon',
        loginSituation: '周年庆活动页领券',
        triggerType: 'manual',
        isChannelCoupon: false,
        isAccessChannelCoupon: false,
        pendingAccessChannelCoupon: null,
      });
    },
    [
      inAppH5,
      marketingPageId,
      refreshAccountInfo,
      setClientTokenReady,
      setQuickCouponDialog,
      setToken,
      setUserInfo,
    ],
  );

  const couponItems = useMemo<MarketingCouponItem[]>(() => {
    const activeCouponConfig = clientCouponConfig || couponConfig;
    if (!hasMarketingCouponList(activeCouponConfig)) {
      return [];
    }

    const displayCount = isBlockPlacement
      ? activeCouponConfig.coupons.length
      : Math.min(Math.max(Number(activeCouponConfig.display_count || 2), 1), 2);

    return activeCouponConfig.coupons
      .map((coupon: MarketingRuntimeCoupon) => {
        const isShippingCoupon = isMarketingShippingCoupon(coupon);
        const transformed = couponDateTransform(
          coupon,
          false,
          tCoupons,
          currency,
          undefined,
          !isShippingCoupon,
        );
        const couponId = String(coupon.id);
        const isCollected =
          isLoggedIn && (coupon.is_get === 2 || claimedCouponIds.includes(couponId));
        const couponName = typeof coupon.name === 'string' ? coupon.name.trim() : '';
        const rawDiscountText = transformed.disCountText;
        const discountText = normalizeMarketingCouponText(rawDiscountText);
        const title = normalizeMarketingCouponText(transformed.title);
        const shippingDescription = isShippingCoupon
          ? normalizeMarketingCouponText(transformed.description) ||
            normalizeMarketingCouponText(tCoupons('standardShipping'))
          : '';
        const description = getMarketingCouponDateRangeText(
          coupon,
          isShippingCoupon ? undefined : transformed.description,
          locale,
        );
        const label = coupon.extension_field?.coupon_label?.tag_text;
        const fallbackDiscountText = normalizeMarketingCouponText(coupon.name);
        const hasDiscountText = isUsefulMarketingCouponText(discountText);
        const resolvedDiscountText = hasDiscountText ? discountText : fallbackDiscountText;
        const isAvailable =
          clientTokenReady &&
          !isPreview &&
          (isLoggedIn ? coupon.is_get === 1 && coupon.code_status === 1 && !isCollected : true);
        const status: MarketingCouponItem['status'] = isCollected
          ? 'saved'
          : isAvailable
            ? 'available'
            : 'disabled';

        return {
          id: coupon.id,
          name: couponName,
          sourceCoupon: coupon,
          discountText: resolvedDiscountText,
          discountLines: getMarketingCouponDiscountLines(
            hasDiscountText ? rawDiscountText : fallbackDiscountText,
          ),
          title: isUsefulMarketingCouponText(title) ? title : '',
          shippingDescription: isUsefulMarketingCouponText(shippingDescription)
            ? shippingDescription
            : '',
          description: isUsefulMarketingCouponText(description) ? description : '',
          ctaText: isCollected ? tCouponComponent('buttons.shop') : tCouponComponent('buttons.get'),
          status,
          isCollectible: isAvailable,
          isCollecting: collectingCouponIds.includes(couponId),
          label:
            typeof label === 'string' && label.trim()
              ? label.trim()
              : coupon.is_first_order === 1
                ? tCouponComponent('labels.firstOrderOnly')
                : '',
        };
      })
      .filter((coupon) => isUsefulMarketingCouponText(coupon.discountText))
      .slice(0, displayCount);
  }, [
    claimedCouponIds,
    clientTokenReady,
    clientCouponConfig,
    collectingCouponIds,
    couponConfig,
    currency,
    isBlockPlacement,
    isLoggedIn,
    isPreview,
    locale,
    tCouponComponent,
    tCoupons,
  ]);

  const handleCollectCoupons = useCallback(
    async (coupons: MarketingCouponItem[], mode: 'single' | 'batch' = 'batch') => {
      if (!isBlockPlacement) {
        return;
      }

      if (coupons.length === 1 && coupons[0]?.status === 'saved') {
        scrollToBrandProductsSection();
        return;
      }

      if (isPreview || !marketingPageId) {
        return;
      }

      if (!clientTokenReady) {
        return;
      }

      if (!isLoggedIn) {
        openCouponLoginDialog(coupons);
        return;
      }

      const availableCoupons = coupons.filter(
        (coupon) => coupon.isCollectible && !coupon.isCollecting,
      );
      const couponIds = availableCoupons.map((coupon) => coupon.id);
      if (couponIds.length === 0) {
        return;
      }

      const collectingIds = couponIds.map(String);
      setCollectingCouponIds((prev) => Array.from(new Set([...prev, ...collectingIds])));

      try {
        const response = await receiveMarketingCoupons({ marketingPageId, couponIds });
        const successIds = (response.success_coupon_ids || []).map(String);

        if (successIds.length > 0) {
          setClaimedCouponIds((prev) => Array.from(new Set([...prev, ...successIds])));
          if (mode === 'batch') {
            toast.success(tMarketing('coupon.claimAllSuccess', { count: successIds.length }));
          }
        } else {
          toast.error(response.failed_reasons?.[0]?.message || tMarketing('coupon.unavailable'));
        }
      } catch (error) {
        logger.error('Marketing coupon collect failed:', error);
        toast.error(tMarketing('coupon.getFailed'));
      } finally {
        setCollectingCouponIds((prev) => prev.filter((id) => !collectingIds.includes(id)));
      }
    },
    [
      isBlockPlacement,
      clientTokenReady,
      isLoggedIn,
      isPreview,
      marketingPageId,
      openCouponLoginDialog,
      scrollToBrandProductsSection,
      tMarketing,
    ],
  );
  const syncSwiperEdgeState = useCallback((swiper: SwiperType) => {
    setIsBeginning((current) => (current === swiper.isBeginning ? current : swiper.isBeginning));
    setIsEnd((current) => (current === swiper.isEnd ? current : swiper.isEnd));
  }, []);

  if (couponItems.length === 0) {
    return null;
  }

  const isSingleFlowCoupon = !isBannerPlacement && couponItems.length === 1;
  const isDoubleFlowCoupon = !isBannerPlacement && couponItems.length > 1;
  const isWapBlockPlacement = isBlockPlacement && isWap;
  const savedIconSrc = getMarketingCouponSavedIcon(locale);
  const shouldWrapWapDiscountText =
    isWapBlockPlacement && !MARKETING_COUPON_ENGLISH_LOCALES.has(locale);
  const shouldUseWideWapCoupon =
    isWapBlockPlacement && MARKETING_COUPON_WIDE_WAP_LOCALES.has(locale);
  const shouldUseCouponSwiper = isBlockPlacement;
  const shouldShowCouponSwiperNavigation = shouldUseCouponSwiper && !isWapBlockPlacement;
  const sectionClassName = isBlockPlacement
    ? isWapBlockPlacement
      ? 'relative z-10 mx-[8px] mt-[8px] flex w-auto flex-col items-center gap-[12px] overflow-hidden rounded-[8px] px-[12px] pb-[8px] pt-[12px]'
      : 'relative z-10 mx-auto my-[20px] w-[calc(100%_-_96px)] max-w-[1680px] rounded-[12px] px-[24px] py-[24px] min-[1281px]:w-[calc(100%_-_240px)] min-[1281px]:px-[40px]'
    : isBannerPlacement
      ? 'pointer-events-none absolute bottom-0 left-0 right-0 z-20 hidden bg-[rgba(0,0,0,0.60)] px-[48px] py-[20px] backdrop-blur-[6.25px] min-[1025px]:block'
      : `relative z-10 bg-white pt-[8px] min-[1025px]:hidden ${isWebPage ? 'px-[48px]' : 'px-[8px]'}`;
  const listClassName = isBlockPlacement
    ? isWapBlockPlacement
      ? 'scrollbar-hide flex w-full flex-nowrap gap-[6px] overflow-x-auto overscroll-x-contain [-webkit-overflow-scrolling:touch]'
      : 'scrollbar-hide mt-[24px] flex w-full flex-nowrap gap-[12px] overflow-x-auto overscroll-x-contain [-webkit-overflow-scrolling:touch]'
    : isBannerPlacement
      ? couponItems.length > 1
        ? 'mx-auto flex w-full max-w-[970px] items-center justify-center gap-[10px]'
        : 'mx-auto flex w-fit max-w-full items-center justify-center gap-[10px]'
      : `mx-auto grid w-full max-w-[610px] gap-[8px] md:max-w-[920px] lg:max-w-none ${
          couponItems.length > 1 ? 'grid-cols-2' : 'grid-cols-1'
        }`;
  const cardClassName = isBlockPlacement
    ? isWapBlockPlacement
      ? `relative isolate flex h-[116px] ${
          shouldUseWideWapCoupon ? 'w-[132px]' : 'w-[104px]'
        } shrink-0 flex-col items-center justify-end gap-[12px] rounded-[6px] bg-white pb-[8px] text-primary-black`
      : 'relative isolate flex h-[82px] w-[clamp(320px,calc(10.9375vw+180px),390px)] shrink-0 items-center gap-[4px] rounded-[6px] bg-white pr-[12px] text-primary-black'
    : isBannerPlacement
      ? couponItems.length > 1
        ? 'relative isolate flex h-[70px] min-w-0 flex-1 items-center rounded-[6px] bg-[#e3deff] px-[30px] text-primary-black'
        : 'relative isolate flex h-[70px] w-[480px] max-w-full items-center rounded-[6px] bg-[#e3deff] px-[30px] text-primary-black'
      : isSingleFlowCoupon
        ? 'relative isolate flex min-h-[82px] flex-row items-center rounded-[4px] bg-[#e3deff] px-[20px] py-[10px] text-primary-black'
        : 'relative isolate flex min-h-[82px] flex-col justify-center rounded-[4px] bg-auxiliary-light-purple px-[12px] py-[10px] text-primary-black';
  const discountClassName = isBlockPlacement
    ? isWapBlockPlacement
      ? `font-be text-[#333333] ${
          shouldWrapWapDiscountText ? 'text-[18px] leading-[20px]' : 'text-[20px] leading-[24px]'
        }`
      : 'flex size-[82px] items-center justify-center px-[6px] font-bb text-[20px] leading-[22px]'
    : isBannerPlacement
      ? 'font-be text-[28px] leading-[24px]'
      : isSingleFlowCoupon
        ? 'flex w-[86px] items-center justify-center font-be text-[20px] leading-[22px]'
        : 'font-bb text-[18px] leading-[20px] text-left';
  const titleClassName = isBlockPlacement
    ? isWapBlockPlacement
      ? shouldUseWideWapCoupon
        ? 'max-w-[120px] whitespace-nowrap font-br text-[11px] leading-[13px] text-[#222222]'
        : 'max-w-[96px] font-br text-[11px] leading-[13px] text-[#222222] [word-break:break-word]'
      : 'line-clamp-2 max-w-full font-bb text-[14px] leading-[18px] text-primary-black [word-break:break-word]'
    : isBannerPlacement
      ? 'font-bb text-[16px] leading-[19px] text-primary-black'
      : isSingleFlowCoupon
        ? 'line-clamp-2 max-w-full font-bb text-[14px] leading-[17px] text-primary-black [word-break:break-word]'
        : 'mt-[4px] font-br text-[12px] leading-[14px] text-primary-black';
  const descriptionClassName = isBlockPlacement
    ? isWapBlockPlacement
      ? `${
          shouldUseWideWapCoupon ? 'max-w-[120px]' : 'max-w-[96px]'
        } font-br text-[11px] leading-[13px] text-primary-black [word-break:break-word]`
      : 'max-w-full truncate font-br text-[12px] leading-[14px] text-[#808080]'
    : isBannerPlacement
      ? 'mt-[8px] font-br text-[12px] leading-[14px] text-auxiliary-text'
      : isSingleFlowCoupon
        ? 'mt-[8px] max-w-full truncate font-br text-[12px] leading-[12px] text-auxiliary-text'
        : 'mt-[4px] font-br text-[10px] leading-[12px] text-auxiliary-text';
  const labelClassName = isBannerPlacement
    ? 'absolute left-0 top-0 rounded-br-[6px] rounded-tl-[6px] bg-[#c7ff57] px-[8px] py-[1px] font-ir text-[12px] leading-[14px] text-[#282828] [-webkit-font-smoothing:antialiased] [-moz-osx-font-smoothing:grayscale]'
    : isBlockPlacement
      ? `absolute left-0 top-0 rounded-br-[6px] rounded-tl-[6px] bg-[#c7ff57] px-[8px] py-[1px] font-br ${
          shouldUseWideWapCoupon ? 'text-[10px]' : 'text-[11px]'
        } leading-[14px] text-[#282828]`
      : 'absolute left-0 top-0 rounded-br-[6px] rounded-tl-[6px] bg-[#c7ff57] px-[6px] py-[1px] text-[10px] leading-[12px] text-[#282828] [-webkit-font-smoothing:antialiased] [-moz-osx-font-smoothing:grayscale]';
  const ctaClassName = isWapBlockPlacement
    ? 'flex h-[20px] min-w-[52px] shrink-0 items-center justify-center whitespace-nowrap rounded-[12px] px-[11px] font-br text-[11px] uppercase leading-[16px]'
    : 'flex h-[24px] min-w-[54px] shrink-0 items-center justify-center whitespace-nowrap rounded-[40px] px-[12px] font-br text-[12px] uppercase leading-[14px]';
  const collectableCouponItems = couponItems.filter((coupon) => coupon.isCollectible);
  const isCollectingAll =
    collectableCouponItems.length > 0 &&
    collectableCouponItems.every((coupon) => coupon.isCollecting);
  const hasCollectableCouponItems = collectableCouponItems.length > 0;
  const couponBlockTitle = couponBlock?.title || '';
  const collectAllButtonText = tMarketing('coupon.claimAll');
  const shouldShowCollectAllButton = Boolean(
    !isPreview &&
      clientTokenReady &&
      (isLoggedIn ? hasCollectableCouponItems : couponItems.length > 0),
  );
  const collectAllButtonClassName = isWapBlockPlacement
    ? `flex h-[32px] w-[264px] min-w-[128px] shrink-0 items-center justify-center rounded-[32px] bg-primary-black px-[46px] font-bb text-[13px] leading-[16px] text-white ${
        isCollectingAll ? 'cursor-default' : 'cursor-pointer'
      }`
    : `flex h-[32px] w-[210px] shrink-0 items-center justify-center rounded-[32px] bg-primary-black px-[28px] font-bb text-[14px] leading-[18px] text-white ${
        isCollectingAll ? 'cursor-default' : 'cursor-pointer'
      }`;
  const renderCollectAllButton = () =>
    shouldShowCollectAllButton ? (
      <button
        type="button"
        className={collectAllButtonClassName}
        disabled={isCollectingAll}
        onClick={() => {
          trackCouponButtonClick(collectAllButtonText);
          handleCollectCoupons(couponItems, 'batch');
        }}
      >
        {isCollectingAll ? '...' : collectAllButtonText}
      </button>
    ) : null;
  const renderCouponCard = (coupon: MarketingCouponItem, index: number) => (
    <MarketingCouponCard
      key={coupon.id}
      coupon={coupon}
      index={index}
      isWap={isWap}
      isSingleFlowCoupon={isSingleFlowCoupon}
      isDoubleFlowCoupon={isDoubleFlowCoupon}
      isBannerPlacement={isBannerPlacement}
      isBlockPlacement={isBlockPlacement}
      shouldWrapWapDiscountText={shouldWrapWapDiscountText}
      shouldUseWideWapCoupon={shouldUseWideWapCoupon}
      cardClassName={cardClassName}
      discountClassName={discountClassName}
      titleClassName={titleClassName}
      descriptionClassName={descriptionClassName}
      labelClassName={labelClassName}
      ctaClassName={ctaClassName}
      savedIconSrc={savedIconSrc}
      notchBackgroundColor={couponBlock?.background_color}
      couponExposureModuleName={couponExposureModuleName}
      onCouponCollect={(coupon) => {
        onCouponClick?.(
          {
            id: coupon.id,
            name: coupon.name,
          },
          index,
          couponExposureModuleName,
          coupon.ctaText,
        );
        handleCollectCoupons([coupon], 'single');
      }}
      onCouponExposure={onCouponExposure}
    />
  );

  return (
    <div
      className={sectionClassName}
      style={isBlockPlacement ? { backgroundColor: couponBlock?.background_color } : undefined}
    >
      {isBlockPlacement && (
        <div
          className={
            isWapBlockPlacement
              ? 'flex w-full flex-col items-start'
              : 'flex w-full items-center justify-between gap-[12px]'
          }
        >
          <h2
            className={
              isWapBlockPlacement
                ? 'font-bb text-[18px] leading-[22px] text-primary-black'
                : 'font-bb text-[24px] leading-[30px] text-primary-black'
            }
          >
            {couponBlockTitle}
          </h2>
          {!isWapBlockPlacement && renderCollectAllButton()}
        </div>
      )}
      {shouldUseCouponSwiper ? (
        <div
          className={
            isWapBlockPlacement
              ? '-ml-[12px] w-[calc(100%_+_24px)] self-start'
              : 'group relative -ml-[24px] mt-[24px] w-[calc(100%_+_48px)] min-[1281px]:-ml-[40px] min-[1281px]:w-[calc(100%_+_80px)]'
          }
        >
          <Swiper
            modules={shouldShowCouponSwiperNavigation ? [Navigation] : undefined}
            slidesPerView="auto"
            spaceBetween={isWapBlockPlacement ? 6 : 12}
            slidesOffsetBefore={isWapBlockPlacement ? 12 : 24}
            slidesOffsetAfter={isWapBlockPlacement ? 12 : 24}
            breakpoints={
              isWapBlockPlacement
                ? undefined
                : {
                    1281: {
                      slidesOffsetBefore: 40,
                      slidesOffsetAfter: 40,
                    },
                  }
            }
            navigation={
              shouldShowCouponSwiperNavigation
                ? {
                    nextEl: '.marketing-coupon-next',
                    prevEl: '.marketing-coupon-prev',
                  }
                : undefined
            }
            onSwiper={(swiper) => {
              couponSwiperRef.current = swiper;
              syncSwiperEdgeState(swiper);
            }}
            onSlideChange={syncSwiperEdgeState}
            onProgress={syncSwiperEdgeState}
            onReachBeginning={syncSwiperEdgeState}
            onReachEnd={syncSwiperEdgeState}
            onFromEdge={syncSwiperEdgeState}
            onTransitionEnd={syncSwiperEdgeState}
            className="w-full max-w-full min-w-0 !overflow-hidden"
          >
            {couponItems.map((coupon, index) => (
              <SwiperSlide key={coupon.id} className="!w-auto">
                {renderCouponCard(coupon, index)}
              </SwiperSlide>
            ))}
          </Swiper>
          {shouldShowCouponSwiperNavigation && (
            <>
              <MarketingSwiperArrowButton
                className="marketing-coupon-prev left-[8px]"
                direction="prev"
                hidden={isBeginning}
                label="Previous"
                onClick={() => couponSwiperRef.current?.slidePrev()}
              />
              <MarketingSwiperArrowButton
                className="marketing-coupon-next right-[8px]"
                direction="next"
                hidden={isEnd}
                label="Next"
                onClick={() => couponSwiperRef.current?.slideNext()}
              />
            </>
          )}
        </div>
      ) : (
        <div className={listClassName}>
          {couponItems.map((coupon, index) => renderCouponCard(coupon, index))}
        </div>
      )}
      {isWapBlockPlacement && renderCollectAllButton()}
    </div>
  );
}
